import React, { useState } from 'react';
import type { BatchUploadFileInput, BatchUploadPayload, DuplicateCheckResultItem } from '../types';

interface Props {
  onUploadSubmit: (payload: BatchUploadPayload) => Promise<void>;
  onCheckDuplicates: (
    files: Array<{ filename: string; checksum: string }>
  ) => Promise<DuplicateCheckResultItem[]>;
}

export const MultiFileUploadView: React.FC<Props> = ({
  onUploadSubmit,
  onCheckDuplicates,
}) => {
  const [category, setCategory] = useState('articles');
  const [folder, setFolder] = useState('Шаблоны статей');
  const [entityType, setEntityType] = useState('article');
  const [entityId, setEntityId] = useState('ART-2026-08');
  const [scopeId, setScopeId] = useState('SYSTEM_OWNER');
  const [comment, setComment] = useState('');
  const [enableAi, setEnableAi] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState<BatchUploadFileInput[]>([]);
  const [duplicateAlerts, setDuplicateAlerts] = useState<DuplicateCheckResultItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccessMsg, setUploadSuccessMsg] = useState<string | null>(null);

  const readFilePayload = (file: File): Promise<BatchUploadFileInput> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const generatedHash = `sha256_${file.name.replace(/[^a-zA-Z0-9]/g, '_')}_${file.size}`;
        resolve({
          original_name: file.name,
          display_name: file.name.replace(/\.[^/.]+$/, ''),
          mime_type: file.type || 'application/octet-stream',
          size_bytes: file.size,
          checksum: generatedHash,
          duplicate_action: 'create_new',
          content_preview: `Содержимое загруженного файла ${file.name}`,
          content_base64: String(reader.result || ''),
        });
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const filesArray = Array.from(e.target.files);
    const newItems = await Promise.all(filesArray.map(readFilePayload));

    setSelectedFiles((prev) => [...prev, ...newItems]);

    const checkPayload = newItems.map((f) => ({
      filename: f.original_name,
      checksum: f.checksum,
    }));

    try {
      const dupResults = await onCheckDuplicates(checkPayload);
      const foundDups = dupResults.filter((d) => d.is_duplicate);
      if (foundDups.length > 0) {
        setDuplicateAlerts(foundDups);
      }
    } catch {
      setDuplicateAlerts([]);
    }
  };

  const handleDuplicateActionChange = (
    filename: string,
    action: 'create_new' | 'new_version' | 'skip',
    existingFileId?: string
  ) => {
    setSelectedFiles((prev) =>
      prev.map((f) => {
        if (f.original_name === filename) {
          return {
            ...f,
            duplicate_action: action,
            existing_file_id: existingFileId,
          };
        }
        return f;
      })
    );
    setDuplicateAlerts((prev) => prev.filter((d) => d.filename !== filename));
  };

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) return;
    setIsUploading(true);
    setUploadSuccessMsg(null);

    const payload: BatchUploadPayload = {
      category,
      folder,
      entity_type: entityType || undefined,
      entity_id: entityId || undefined,
      scope_id: scopeId,
      comment: comment || undefined,
      enable_ai_indexing: enableAi,
      files: selectedFiles,
    };

    try {
      await onUploadSubmit(payload);
      setUploadSuccessMsg(`Успешно загружено файлов: ${selectedFiles.length}`);
      setSelectedFiles([]);
      setDuplicateAlerts([]);
    } catch {
      setIsUploading(false);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="multi-file-upload-view" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div
        className="batch-upload-box"
        onClick={() => document.getElementById('batch-file-input')?.click()}
      >
        <div className="batch-upload-icon">+</div>
        <h3 style={{ fontSize: '1.2rem', fontWeight: 600, color: '#f8fafc', margin: '0 0 0.5rem 0' }}>
          Перетащите файлы сюда или нажмите для выбора
        </h3>
        <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
          Разрешённые форматы: Word (.docx), Excel (.xlsx), CSV, PDF, изображения, аудио и ZIP-архивы.
        </p>
        <input
          id="batch-file-input"
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <button
          type="button"
          className="btn-open-preview"
          onClick={(e) => {
            e.stopPropagation();
            document.getElementById('batch-file-input')?.click();
          }}
        >
          Выбрать файлы на диске
        </button>
      </div>

      <div style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '1.25rem' }}>
        <h4 style={{ color: '#f8fafc', margin: '0 0 1rem 0', fontSize: '0.98rem', fontWeight: 600 }}>
          Общие метаданные для пакета
        </h4>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.35rem' }}>
              Категория
            </label>
            <select
              className="library-select"
              style={{ width: '100%' }}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="articles">Статьи и публикации</option>
              <option value="doctoral">Докторантура</option>
              <option value="faculty">Обучение и кафедра</option>
              <option value="templates">Шаблоны и образцы</option>
              <option value="prompts">Промпты и инструкции</option>
              <option value="projects">Проекты и гранты</option>
              <option value="client_orders">Клиенты / заказы</option>
              <option value="ai_knowledge">AI · База знаний</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.35rem' }}>
              Папка
            </label>
            <input
              type="text"
              className="library-search-input"
              style={{ width: '100%' }}
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              placeholder="Например: Докторантура / 03 Научная стажировка"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.35rem' }}>
              Привязка к сущности
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select
                className="library-select"
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
              >
                <option value="article">Статья</option>
                <option value="doctoral_stage">Этап докторантуры</option>
                <option value="project">Проект / грант</option>
                <option value="client_order">Заказ клиента</option>
                <option value="request">Заявка</option>
              </select>
              <input
                type="text"
                className="library-search-input"
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                placeholder="ID объекта"
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.35rem' }}>
              Права доступа (Scope)
            </label>
            <select
              className="library-select"
              style={{ width: '100%' }}
              value={scopeId}
              onChange={(e) => setScopeId(e.target.value)}
            >
              <option value="SYSTEM_OWNER">Владелец системы (полный)</option>
              <option value="OPERATIONS_PARTNER">Операционный партнёр</option>
              <option value="MENTOR_PARTNER">Научный партнёр / коллега</option>
              <option value="DEPARTMENT_HEAD">Заведующий кафедрой</option>
              <option value="ADVISOR">Научный консультант</option>
              <option value="LEARNER">Докторант / Обучающийся</option>
              <option value="AUTHOR">Автор / Клиент</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.35rem' }}>
            Комментарий к пакету
          </label>
          <input
            type="text"
            className="library-search-input"
            style={{ width: '100%' }}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Краткое описание загружаемых материалов..."
          />
        </div>

        <div style={{ marginTop: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            id="ai-indexing-check"
            type="checkbox"
            checked={enableAi}
            onChange={(e) => setEnableAi(e.target.checked)}
          />
          <label htmlFor="ai-indexing-check" style={{ fontSize: '0.85rem', color: '#cbd5e1', cursor: 'pointer' }}>
            Включить индексацию для Super AI и ролевых ассистентов
          </label>
        </div>
      </div>

      {duplicateAlerts.map((dup) => (
        <div key={dup.filename} className="duplicate-alert-banner">
          <div className="duplicate-alert-title">
            <span>⚠</span>
            <span>Обнаружен точный дубликат по контрольной сумме: {dup.filename}</span>
          </div>
          <p style={{ fontSize: '0.85rem', color: '#cbd5e1', margin: 0 }}>
            В системе уже зарегистрирован документ: <strong>{dup.existing_display_name}</strong> ({dup.existing_version_no}).
            Выберите действие для этого файла:
          </p>
          <div className="duplicate-actions">
            <button
              type="button"
              className="btn-open-preview"
              onClick={() => handleDuplicateActionChange(dup.filename, 'new_version', dup.existing_file_id || undefined)}
            >
              Создать новую версию существующего
            </button>
            <button
              type="button"
              className="btn-download-action"
              onClick={() => handleDuplicateActionChange(dup.filename, 'create_new')}
            >
              Загрузить как отдельный документ
            </button>
            <button
              type="button"
              className="action-icon-btn"
              onClick={() => handleDuplicateActionChange(dup.filename, 'skip')}
            >
              Пропустить
            </button>
          </div>
        </div>
      ))}

      {selectedFiles.length > 0 && (
        <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '1.25rem' }}>
          <h4 style={{ color: '#f8fafc', margin: '0 0 1rem 0', fontSize: '0.95rem', fontWeight: 600 }}>
            Файлы в очереди на загрузку ({selectedFiles.length})
          </h4>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {selectedFiles.map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.65rem 0.85rem',
                  background: 'rgba(30, 41, 59, 0.4)',
                  borderRadius: '8px',
                }}
              >
                <div>
                  <span style={{ fontWeight: 600, color: '#f8fafc' }}>{item.original_name}</span>
                  <span style={{ fontSize: '0.78rem', color: '#94a3b8', marginLeft: '0.75rem' }}>
                    {Math.round((item.size_bytes || 0) / 1024)} KB · Действие: {item.duplicate_action}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.75rem', color: '#34d399', background: 'rgba(52, 211, 153, 0.1)', padding: '0.15rem 0.45rem', borderRadius: '4px' }}>
                    Проверка безопасности: PASSED
                  </span>
                  <button
                    type="button"
                    className="action-icon-btn"
                    onClick={() => setSelectedFiles((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button
              type="button"
              className="action-icon-btn"
              onClick={() => setSelectedFiles([])}
              disabled={isUploading}
            >
              Очистить
            </button>
            <button
              type="button"
              className="btn-open-preview"
              style={{ background: '#0284c7', color: '#ffffff', padding: '0.6rem 1.25rem' }}
              onClick={handleSubmit}
              disabled={isUploading}
            >
              {isUploading ? 'Загрузка...' : `Загрузить ${selectedFiles.length} файлов`}
            </button>
          </div>
        </div>
      )}

      {uploadSuccessMsg && (
        <div style={{ background: 'rgba(52, 211, 153, 0.15)', border: '1px solid rgba(52, 211, 153, 0.3)', color: '#34d399', padding: '0.85rem 1rem', borderRadius: '10px' }}>
          ✓ {uploadSuccessMsg}
        </div>
      )}
    </div>
  );
};
